import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import AnalyticsParameterTypeDropdown from 'insights/parameters/AnalyticsParameterTypeDropdown';
import AnalyticsParameterValueEditor, {InputProps} from 'insights/parameters/AnalyticsParameterValueEditor';
import getParameterTypeAndConfigSetting from 'insights/parameters/data/getParameterTypeAndConfigSetting';
import {ParameterTypeToTSType} from 'insights/parameters/data/ValueValidators';
import {
  PARAMETER_TYPE_TO_CONFIG_TYPENAME,
  PARAMETER_TYPE_ALLOWS_MULTIPLE_VALUES,
} from 'insights/parameters/ParameterConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Checkbox, {CheckboxSize} from 'ui-library/Checkbox';
import FieldError from 'ui-library/FieldError';
import {LABEL_STYLES} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import {JSONValue} from 'util/Types';
import {InputChangeEvent} from 'util/Types';

import {AnalyticsParameterConfigGraphQL as ParameterConfig} from '../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';

export interface EditAnalyticsParametersModalRowProps {
  config: ParameterConfig;
  value?: AnalyticsParameterValue;
  valueJsonIsValid: boolean; // TODO make not optional
  onUpdateConfig: (newConfig: ParameterConfig) => void;
  onUpdateValue: (configId: string, newValue: JSONValue) => void;
  writable: boolean;
}

export default function EditAnalyticsParametersModalRow(
  props: EditAnalyticsParametersModalRowProps
): JSX.Element {
  const {config, value, valueJsonIsValid, onUpdateConfig, onUpdateValue} = props;
  const typeAndConfigSetting = getParameterTypeAndConfigSetting(config);
  const {type, configSetting} = typeAndConfigSetting;

  const inputProps: InputProps<typeof type> = {
    valueJson: value!.valueJson as ParameterTypeToTSType[typeof type],
    paramConfigSetting: configSetting,
    onChange: (valueJson) => {
      onUpdateValue(config.id, valueJson);
    },
    allowAutoFocus: false,
  };

  return (
    <FormGroup>
      <Row
        className={css(styles.row)}
        dataTestAttrs={{
          component: 'EditAnalyticsParametersModalRow',
          element: 'root',
          key: config.systemName,
        }}
        dataAmpSafe={false}
      >
        <Col sm={3}>
          <TextField
            label="Name"
            value={config.systemName}
            inputClassName={css(styles.inputHeightFix)}
            disabled={!props.writable}
            labelStyle={LABEL_STYLES.HIDDEN}
            onChange={(event: InputChangeEvent) => {
              onUpdateConfig({
                ...config,
                systemName: event.target.value,
              });
            }}
          />
        </Col>
        <Col sm={3}>
          <AnalyticsParameterTypeDropdown
            parameterConfig={typeAndConfigSetting}
            onChange={(newTypeAndConfig) => {
              onUpdateConfig({
                id: config.id,
                systemName: config.systemName,
                __typename: PARAMETER_TYPE_TO_CONFIG_TYPENAME[newTypeAndConfig.type],
                ...newTypeAndConfig.configSetting,
              } as ParameterConfig);
            }}
            disabled={!props.writable}
          />
        </Col>
        <Col sm={4}>
          <AnalyticsParameterValueEditor paramType={type} inputProps={inputProps} />
          {!valueJsonIsValid && <FieldError message="Please select a valid value" />}
        </Col>
        <Tooltip
          disabled={!props.writable || PARAMETER_TYPE_ALLOWS_MULTIPLE_VALUES[type]}
          title="Multi-value parameters are not supported for this parameter type"
          placement={TooltipPlacement.TOP}
        >
          <Col sm={2} className={css(styles.checkboxContainer)}>
            <Checkbox
              size={CheckboxSize.SMALL}
              checked={config.isMulti}
              onChange={(e) => {
                onUpdateConfig({
                  ...config,
                  isMulti: e.target.checked,
                } as ParameterConfig);
              }}
              disabled={!props.writable || !PARAMETER_TYPE_ALLOWS_MULTIPLE_VALUES[type]}
              dataTestAttrs={{
                component: 'EditAnalyticsParametersModalRow',
                element: 'multiSelectCheckbox',
              }}
            />
          </Col>
        </Tooltip>
      </Row>
    </FormGroup>
  );
}

const styles = StyleSheet.create({
  inputHeightFix: {
    height: 36,
  },
  checkboxContainer: {
    textAlign: 'center',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
  },
});
